/**
 * "My billing" (GET /billing/my/...): what the current organisation owes the one above
 * it, what its businesses owe it, and its prepaid wallet. The shapes mirror the backend
 * records in MyBilling.java; the helpers are kept out of the page so they can be tested.
 */
import { istMonth } from './closeBooksRules';

export type MyBillingView = 'PROVIDER' | 'BUSINESS' | 'PLATFORM';

export interface MyPeriod {
  month: string;
  monthToDate: boolean;
  from: string;
  to: string;
  startUtc: string;
  endUtc: string;
}

export interface MyTax {
  treatment: 'CGST_SGST' | 'IGST' | 'ESTIMATED_GST' | 'ZERO_RATED_LUT' | 'INTERNAL_STATEMENT' | string;
  ratePercent: number;
  estimated: boolean;
  note: string;
}

export interface MyLine {
  businessId: string | null;
  businessName: string;
  direct: boolean;
  testCalls: boolean;
  serviceType: string | null;
  carrier: string;
  charges: number;
  billedMinutes: number;
  amount: number;
}

export interface MyBucket {
  key: string;
  label: string;
  charges: number;
  billedMinutes: number;
  amount: number;
}

export interface MyDay {
  day: string;
  charges: number;
  billedMinutes: number;
  amount: number;
}

export interface MyCharges {
  charges: number;
  billedMinutes: number;
  base: number;
  tax: MyTax;
  taxAmount: number;
  total: number;
  byBusiness: MyBucket[];
  byServiceType: MyBucket[];
  byCarrier: MyBucket[];
  lines: MyLine[];
  daily: MyDay[];
}

export interface MyAccountMovement {
  billingType: string | null;
  currency: string;
  openingBalance: number;
  paymentsAndTopUps: number;
  usage: number;
  other: number;
  closingBalance: number;
}

export interface MyInvoiceRow {
  id: number;
  invoiceNumber: string;
  kind: string | null;
  status: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  subtotal: number;
  taxAmount: number;
  total: number;
  amountPaid: number;
  outstanding: number;
  currency: string;
}

export interface MyPaymentRow {
  id: number;
  reference: string;
  invoiceId: number | null;
  amount: number;
  currency: string;
  method: string | null;
  paymentDate: string;
  successful: boolean;
}

export interface MyPayable {
  payeeOrganizationId: string | null;
  payeeName: string | null;
  documentType: string | null;
  hasAccount: boolean;
  account: MyAccountMovement | null;
  charges: MyCharges;
  invoices: MyInvoiceRow[];
  outstandingInvoices: MyInvoiceRow[];
  outstanding: number;
  payments: MyPaymentRow[];
}

export interface MyGrantRow {
  id: number;
  source: string;
  status: string;
  originalAmount: number;
  remainingAmount: number;
  expiresAt: string | null;
  description: string | null;
}

export interface MyWallet {
  hasAccount: boolean;
  prepaid: boolean;
  billingType: string | null;
  currency: string | null;
  note: string | null;
  creditLimit: number | null;
  balance: number | null;
  openHolds: number | null;
  available: number | null;
  grantedRemaining: number | null;
  overdraft: number | null;
  grants: MyGrantRow[];
  expiringSoonAmount: number | null;
  expiringSoon: MyGrantRow[];
  recentTopUps: { transactionId: number; amount: number; occurredAt: string; description: string | null }[];
  burnPerDay: number | null;
  runsOutInDays: number | null;
  runsOutOn: string | null;
  alerts: {
    tiers?: { tier: string; thresholdAmount: number; crossed: boolean; status: string | null; sentAt: string | null }[];
  } | null;
  autoRecharge: { enabled: boolean; threshold: number | null; amount: number | null; active: boolean; note: string } | null;
}

export interface MySummary {
  organizationId: string;
  organizationName: string;
  view: MyBillingView;
  period: MyPeriod;
  payable: MyPayable;
  hasReceivable: boolean;
  wallet: MyWallet;
}

export interface MyBusinessReceivable {
  businessId: string;
  businessName: string;
  metered: boolean;
  charges: number;
  billedMinutes: number;
  amount: number;
  tax: MyTax;
  taxAmount: number;
  total: number;
  upstreamMinutes: number;
  upstreamCost: number;
  margin: number;
}

export interface MyReceivables {
  organizationId: string;
  organizationName: string;
  period: MyPeriod;
  businesses: MyBusinessReceivable[];
  charges: number;
  billedMinutes: number;
  amount: number;
  taxAmount: number;
  total: number;
  upstreamCost: number;
  margin: number;
}

/**
 * Who sees "My billing" (backend @PreAuthorize on /billing/my/**), lower-cased, both
 * spellings. operator_user and service_user are not billing roles.
 */
export const MY_BILLING_ROLES = [
  'master_admin', 'platform_admin',
  'super_admin', 'provider_owner', 'service_admin', 'provider_admin',
  'operator_admin', 'business_admin', 'business_owner',
];
export const canSeeMyBilling = (role: string | null | undefined): boolean =>
  MY_BILLING_ROLES.includes((role || '').toLowerCase());

/** The last {@code count} IST months, newest first, `YYYY-MM`. */
export const recentMonths = (now: Date = new Date(), count = 13): string[] => {
  const [y, m] = istMonth(now).split('-').map(Number);
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
};

/** "Payable to Green Giraffe", or a top-of-tree caption. */
export const payableHeading = (s: Pick<MySummary, 'payable'>): string =>
  s.payable.payeeName ? `Payable to ${s.payable.payeeName}` : 'Your own usage';

/** The period caption: "October 2026, to 14 Oct (month to date)". */
export const periodCaption = (p: MyPeriod): string => {
  const [y, m] = p.month.split('-').map(Number);
  const name = new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return p.monthToDate ? `${name}, month to date (to ${p.to}, IST)` : `${name} (IST)`;
};

/** How long a prepaid wallet lasts, in words. */
export const runsOutText = (w: Pick<MyWallet, 'runsOutInDays' | 'burnPerDay'>): string => {
  if (w.runsOutInDays === null || w.runsOutInDays === undefined) {
    return (w.burnPerDay ?? 0) > 0 ? '' : 'No usage in the last 7 days';
  }
  if (w.runsOutInDays === 0) return 'Credit is used up';
  return `Runs out in ~${w.runsOutInDays} day${w.runsOutInDays === 1 ? '' : 's'} at this rate`;
};

/** The tax column's header: "GST 18% (est.)", "No GST (internal statement)", … */
export const taxHeader = (t: MyTax | null | undefined): string => {
  if (!t) return 'GST';
  if (t.treatment === 'INTERNAL_STATEMENT') return 'No GST (internal statement)';
  if (t.treatment === 'ZERO_RATED_LUT') return 'GST 0% (LUT)';
  const kind = t.treatment === 'CGST_SGST' ? 'CGST+SGST' : t.treatment === 'IGST' ? 'IGST' : 'GST';
  return `${kind} ${t.ratePercent}%${t.estimated ? ' (est.)' : ''}`;
};

export const formatMinutes = (n: number | null | undefined): string =>
  n === null || n === undefined ? '-' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
